import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  CButton, CCard, CCardBody, CCol, CContainer, CForm, CFormCheck, CFormInput,
  CFormLabel, CRow, CAlert, CSpinner,
} from '@coreui/react'
import { restablecerClave } from '../api/client'

/**
 * Restablecer contraseña — [RGM] diseño nuevo aprobado por Rafael (el login
 * GX/LowCode no tiene recuperación). Recibe token + email por query params
 * (link del correo de /auth/recuperar-clave) y envía la nueva clave al
 * backend, que valida el token firmado y su expiración.
 */
const RestablecerClavePage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const email = searchParams.get('email') ?? ''

  const [nuevaClave, setNuevaClave] = useState('')
  const [confirmacion, setConfirmacion] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [restablecida, setRestablecida] = useState(false)

  const linkInvalido = !token || !email

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (nuevaClave !== confirmacion) {
      setError('Las contraseñas no coinciden')
      return
    }
    if (nuevaClave.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres')
      return
    }
    // [RGM] 2026-09-16: UsuarioClave es char(15) — máximo 15.
    if (nuevaClave.length > 15) {
      setError('La contraseña no puede tener más de 15 caracteres')
      return
    }

    setLoading(true)
    try {
      await restablecerClave({ email, token, nuevaClave, confirmacion })
      setRestablecida(true)
      setNuevaClave('')
      setConfirmacion('')
    } catch (err: any) {
      setError(err.message ?? 'No se pudo restablecer la contraseña')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-body-tertiary min-vh-100 d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={8} lg={6} xl={5}>
            <div className="d-flex flex-column gap-4">
              <div className="text-center">
                <img src="/logo_siprinia.png" alt="SiprinIA" height={120} />
                <h1 className="h3 fw-bold mb-0 mt-2" style={{ color: 'var(--cui-body-color)' }}>
                  SiprinIA
                </h1>
              </div>
              <CCard className="p-4">
                <CCardBody className="d-flex flex-column gap-4">
                  <h2 className="h5 text-center mb-0">Restablecer contraseña</h2>

                  {linkInvalido ? (
                    <>
                      <CAlert color="danger" className="mb-0">
                        El enlace es inválido o ha expirado.
                      </CAlert>
                      <div className="text-center text-body-secondary">
                        <Link to="/recuperar-clave" className="text-decoration-none">
                          Solicitar un nuevo enlace
                        </Link>
                      </div>
                    </>
                  ) : restablecida ? (
                    <>
                      <CAlert color="success" className="mb-0">
                        Contraseña restablecida. Ya puedes iniciar sesión.
                      </CAlert>
                      <div className="text-center text-body-secondary">
                        <Link to="/login" className="text-decoration-none">Iniciar sesión</Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-center text-body-secondary mb-0">
                        Ingresa tu nueva contraseña para <strong>{email}</strong>
                      </p>
                      {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
                      <CForm className="row gy-3" onSubmit={handleSubmit}>
                        <CCol xs={12}>
                          <CFormLabel htmlFor="res-pass">Nueva contraseña</CFormLabel>
                          <CFormInput
                            id="res-pass"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Mínimo 6 caracteres"
                            value={nuevaClave}
                            maxLength={15}
                            onChange={(e) => setNuevaClave(e.target.value)}
                            autoComplete="new-password"
                            required
                          />
                        </CCol>
                        <CCol xs={12}>
                          <CFormLabel htmlFor="res-pass-confirm">Confirmar contraseña</CFormLabel>
                          <CFormInput
                            id="res-pass-confirm"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Repite la nueva contraseña"
                            value={confirmacion}
                            maxLength={15}
                            onChange={(e) => setConfirmacion(e.target.value)}
                            autoComplete="new-password"
                            required
                          />
                        </CCol>
                        <CCol xs={12}>
                          <CFormCheck
                            id="res-show-pass"
                            label="Mostrar contraseña"
                            checked={showPassword}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowPassword(e.target.checked)}
                          />
                        </CCol>
                        <CCol xs={12}>
                          <CButton color="primary" type="submit" disabled={loading} className="w-100">
                            {loading ? <CSpinner size="sm" /> : 'Restablecer contraseña'}
                          </CButton>
                        </CCol>
                      </CForm>
                    </>
                  )}
                </CCardBody>
              </CCard>
              <div className="text-center text-body-secondary">
                <Link to="/login" className="text-decoration-none">Volver al inicio de sesión</Link>
              </div>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default RestablecerClavePage
